import { useEffect, useMemo, useRef, useState } from "react";

function tagLabel(tag) {
  return String(tag?.display?.value || tag?.id || "").trim();
}

function TagPill({ tag, onRemove }) {
  const color = tag?.display?.color;
  if (color) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full"
        style={{ backgroundColor: color, color: "#fff" }}
      >
        {tagLabel(tag)}
        {onRemove ? (
          <button type="button" onClick={onRemove} className="opacity-80 hover:opacity-100">
            &times;
          </button>
        ) : null}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
      {tagLabel(tag)}
      {onRemove ? (
        <button type="button" onClick={onRemove} className="hover:text-slate-800 dark:hover:text-slate-100">
          &times;
        </button>
      ) : null}
    </span>
  );
}

function ProductAttributeTagSelect({ options = [], value = [], onChange, loading = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const optionsById = useMemo(() => {
    const map = new Map();
    options.forEach((o) => map.set(o.id, o));
    return map;
  }, [options]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const selectedTags = useMemo(
    () => value.map((id) => optionsById.get(id) || { id }),
    [value, optionsById]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.id?.toLowerCase().includes(q) || tagLabel(o).toLowerCase().includes(q)
    );
  }, [options, search]);

  const toggle = (id) => {
    if (selectedSet.has(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        onClick={() => setOpen((o) => !o)}
        className="form-input w-full min-h-[42px] flex flex-wrap gap-1.5 items-center cursor-pointer"
      >
        {selectedTags.length === 0 ? (
          <span className="text-slate-400 dark:text-slate-500">No tags</span>
        ) : (
          selectedTags.map((tag) => (
            <TagPill
              key={tag.id}
              tag={tag}
              onRemove={(e) => {
                e.stopPropagation();
                onChange(value.filter((v) => v !== tag.id));
              }}
            />
          ))
        )}
      </div>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg p-2">
          <input
            type="text"
            autoFocus
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="form-input w-full text-sm mb-2"
          />
          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <p className="text-xs text-slate-500 text-center py-3">Loading tags...</p>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-3">No tags found</p>
            ) : (
              filtered.map((o) => {
                const isSelected = selectedSet.has(o.id);
                return (
                  <div
                    key={o.id}
                    onClick={() => toggle(o.id)}
                    className={`p-2 text-sm cursor-pointer rounded flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-700 ${
                      isSelected ? "bg-indigo-50 dark:bg-indigo-900/20" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="form-checkbox pointer-events-none"
                      checked={isSelected}
                      readOnly
                    />
                    <span className="flex-1 min-w-0">
                      <TagPill tag={o} />
                    </span>
                    <span className="text-xs text-slate-400 font-mono">{o.id}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductAttributeTagSelect;
